import { showApiError, showSuccess } from "@/lib/toast";
import { addUserFirm, deleteUserFirm, getFirm, getUserFirm } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { AppMultiSelect } from "@/components/ui/app-multi-select";

interface Props {
  userId: number;
}

export const UserFirmAssign: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: allFirms, isLoading: isAllFirmsLoading } = useQuery({
    queryKey: ["getFirms"],
    queryFn: () =>
      getFirm({
        numberOfObjectsPerPage: 1000,
        pageNumber: 1,
      }),
  });

  const { data: userFirms, isLoading: isUserFirmsLoading } = useQuery({
    queryKey: ["getUserFirm", userId],
    queryFn: () => getUserFirm(userId),
  });

  const addUserFirmMutation = useMutation({
    mutationFn: (firmId: number) => addUserFirm(userId, firmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUserFirm"] });
      showSuccess(t("common.assignedSuccess"));
    },
    onError: showApiError,
  });

  const deleteUserFirmMutation = useMutation({
    mutationFn: (firmId: number) => deleteUserFirm(userId, firmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUserFirm"] });
      showSuccess(t("common.unassignedSuccess"));
    },
    onError: showApiError,
  });

  const addAllUserFirmMutation = useMutation({
    mutationFn: async (firmIds: number[]) => {
      await Promise.all(firmIds.map((firmId) => addUserFirm(userId, firmId)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUserFirm"] });
      showSuccess(t("common.assignedSuccess"));
    },
    onError: showApiError,
  });

  const deleteAllUserFirmMutation = useMutation({
    mutationFn: async (firmIds: number[]) => {
      await Promise.all(firmIds.map((firmId) => deleteUserFirm(userId, firmId)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUserFirm"] });
      showSuccess(t("common.unassignedSuccess"));
    },
    onError: showApiError,
  });

  const firmOptions = (allFirms?.data.result || []).map((firm) => ({
    value: firm.id,
    label: firm.name,
  }));

  const userFirmsData = userFirms?.data || [];
  const selectedFirmIds = userFirmsData.map((firm) => firm.firm_Id);

  const handleChange = (newValues: (string | number)[]) => {
    const oldValues = new Set(selectedFirmIds);
    const newValuesSet = new Set(newValues);

    // Find firms to add (present in new but not in old)
    const firmsToAdd = [...newValuesSet].filter((id) => !oldValues.has(id as number));

    // Find firms to remove (present in old but not in new)
    const firmsToRemove = [...oldValues].filter((id) => !newValuesSet.has(id));

    if (firmsToAdd.length > 0) {
      addAllUserFirmMutation.mutate(firmsToAdd as number[]);
    }

    if (firmsToRemove.length > 0) {
      const firmIdsToDelete = firmsToRemove
        .map((firmId) => {
          const userFirm = userFirmsData.find((uf) => uf.firm_Id === firmId);
          return userFirm?.id;
        })
        .filter(Boolean) as number[];

      deleteAllUserFirmMutation.mutate(firmIdsToDelete);
    }
  };

  const isLoading =
    addUserFirmMutation.isPending ||
    deleteUserFirmMutation.isPending ||
    addAllUserFirmMutation.isPending ||
    deleteAllUserFirmMutation.isPending ||
    isAllFirmsLoading ||
    isUserFirmsLoading;

  return (
    <div className="relative">
      <AppMultiSelect
        value={selectedFirmIds}
        onChange={handleChange}
        options={firmOptions}
        isLoading={isLoading}
        placeholder={t("entities.firm")}
        label={t("entities.firm")}
      />
    </div>
  );
};
